import clsx from 'clsx';
import type { JSX } from 'react';
import type { PublishedUnitSummary } from '@/lib/types/publicProperty';
import type { AvailabilitiesMapUnitRowContent } from '@/lib/types/siteSettings';
import { formatSquareFeet, formatUnitLabel } from '@/lib/utils/propertyFormatting';
import { formatMonthlyLeaseAmount } from '@/routes/publicProperties/rentFormatting';
import { formatUnitAvailabilityLabel, unitHasIncentives } from './availabilitiesMapListHelpers';
import { applyTemplate } from '@/lib/utils/textTemplates';

export function AvailabilitiesMapPanelUnitRow({
  unit,
  selected,
  content,
  availabilityLabels,
  onSelect,
}: {
  unit: PublishedUnitSummary;
  selected: boolean;
  content: AvailabilitiesMapUnitRowContent;
  availabilityLabels: { immediateLabel: string; negotiableLabel: string };
  onSelect: () => void;
}): JSX.Element {
  const rentLabel = formatMonthlyLeaseAmount(unit);
  const areaLabel = formatSquareFeet(unit.areaSF);
  const availabilityLabel = formatUnitAvailabilityLabel(unit, availabilityLabels);
  const unitDisplay = formatUnitLabel(unit);
  const unitLabel = applyTemplate(content.unitLabelTemplate, {
    unitLabel: unitDisplay,
    unitCode: unit.unitCode,
  });
  const levelLabel = applyTemplate(content.levelLabelTemplate, {
    level: unit.floorLevel,
    floor: unit.floorLevel,
    floorLevel: unit.floorLevel,
  });

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={clsx(
        'flex w-full flex-col gap-2 rounded-2xl border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent',
        selected
          ? 'border-brand-primary bg-brand-primary text-white shadow-sm'
          : 'border-surface-subtle bg-white hover:border-brand-primary/30 hover:bg-brand-primary/5',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <p
          className={clsx('text-sm font-semibold', selected ? 'text-white' : 'text-brand-primary')}
        >
          {unitLabel}
        </p>
        <span
          className={clsx(
            'text-xs font-semibold uppercase tracking-wide',
            selected ? 'text-white/80' : 'text-text-primary/60',
          )}
        >
          {levelLabel}
        </span>
      </div>

      <div
        className={clsx(
          'flex flex-wrap items-center gap-2 text-xs',
          selected ? 'text-white/85' : 'text-text-primary/70',
        )}
      >
        <span className="font-semibold">{rentLabel}</span>
        <span aria-hidden className="text-[10px] opacity-60">
          •
        </span>
        <span>{areaLabel} sq ft</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={clsx('sd-availabilities-unit-chip', selected && 'is-selected')}>
          {availabilityLabel}
        </span>
        {unit.potentiallyDivisible ? (
          <span className={clsx('sd-availabilities-unit-chip', selected && 'is-selected')}>
            {content.divisibleLabel}
          </span>
        ) : null}
        {unitHasIncentives(unit) ? (
          <span
            className={clsx('sd-availabilities-unit-chip is-highlight', selected && 'is-selected')}
          >
            {content.incentivesLabel}
          </span>
        ) : null}
      </div>
    </button>
  );
}
